import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Stack,
  TextField, Select, MenuItem, FormControl, InputLabel, Backdrop, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton, InputAdornment, Divider,
  Menu as MuiMenu, MenuItem as MuiMenuItem, Checkbox, FormControlLabel, RadioGroup, Radio, Popover,
  Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { showSnackbar } from '../store/slices/uiSlice';
import apiService from '../services/api.service';
import CopyPRDialog from '../components/common/CopyPRDialog';
import { fixTwoDigitYear, isDocumentDate } from '../utils/documentDate';
import { ChargeExpenseCodes, chargeDestination, readChargeExpenseCodes, sapExpenseFor } from '../utils/poCharges';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
  EDITOR_COLORS,
  EditorLayout,
  EditorSection,
  SideCard,
  SideFact,
  StatusPill,
  SubmitChecklist,
  addButtonSx,
  secondaryButtonSx,
} from '../components/documentEditor/DocumentEditor';

ModuleRegistry.registerModules([AllCommunityModule]);

type POLine = {
  itemtype: 'Inventory' | 'Service' | 'FixedAsset' | 'Subcontracting';
  itemcode?: string;
  itemdescription: string;
  quantity: number;
  openqty?: number;
  closedqty?: number;
  uomcode?: string;
  unitprice: number;
  accountcode: string;
  warehouse?: string;
  projectcode?: string;
  dim1?: string;
  dim2?: string;
  dim3?: string;
  dim4?: string;
  dim5?: string;
  deliverydate?: string;
  lineremarks?: string;
  discpct?: number;
  discamt?: number;
  sourceprid?: number;
  sourceprlinenum?: number;
};

type ChargeRow = {
  type:
    | 'OTHERS ESTIMATED CHARGES (Finance, Insurance, Installation)'
    | 'FREIGHT CHARGES'
    | 'CUSTOMS CHARGES'
    | 'DELIVERY ORDER'
    | 'LOCAL CLEARANCE CHARGES';
  amount: number;
};

const CreatePO: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((s: any) => s.auth.user);
  const lineGridRef = useRef<any>(null);

  const [submitting, setSubmitting] = useState(false);
  const [requisitionType, setRequisitionType] = useState<'Inventory' | 'Service' | 'FixedAsset' | 'Subcontracting'>('Inventory');
  const [allocationType, setAllocationType] = useState<'Project' | 'CostCenter'>('Project');
  const [allocationCode, setAllocationCode] = useState('');
  const [allocationName, setAllocationName] = useState('');
  const [facilityType, setFacilityType] = useState('NA');
  const [cycle, setCycle] = useState('Project');
  
  const [postingDate, setPostingDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  const [documentDate, setDocumentDate] = useState(() => new Date().toISOString().split('T')[0]);
  
  const [remarks, setRemarks] = useState('');
  const [sellingPrice, setSellingPrice] = useState<number>(0);
  const [headerDiscPct, setHeaderDiscPct] = useState<number>(0);
  const [headerDiscAmt, setHeaderDiscAmt] = useState<number>(0);
  const [taxPct, setTaxPct] = useState<number>(0);
  const [taxAmt, setTaxAmt] = useState<number>(0);
  const [currency, setCurrency] = useState<string>('SAR');
  const [vendorCode, setVendorCode] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [paymentTermCode, setPaymentTermCode] = useState('');
  const [paymentTermName, setPaymentTermName] = useState('');
  const [deliveryType, setDeliveryType] = useState('');
  const [incoterms, setIncoterms] = useState('');
  const [modeOfShipment, setModeOfShipment] = useState('');
  const [requiredDocuments, setRequiredDocuments] = useState('');
  const [consignee, setConsignee] = useState('');
  const [shipmentRequirements, setShipmentRequirements] = useState('');
  const [lines, setLines] = useState<POLine[]>([
    { itemtype: 'Inventory', itemdescription: '', quantity: 1, openqty: 0, closedqty: 0, unitprice: 0, accountcode: '', warehouse: '', deliverydate: '', projectcode: '', dim1: '', dim2: '', dim3: '', dim4: '', dim5: '', lineremarks: '', discpct: 0, discamt: 0 }
  ]);
  const [createdPoId, setCreatedPoId] = useState<number | null>(null);
  const [poNumber, setPoNumber] = useState('');
  const [poStatus, setPoStatus] = useState<string>('Draft');
  const [lastWorkflowAction, setLastWorkflowAction] = useState<string>('');
  const [lastWorkflowComment, setLastWorkflowComment] = useState<string>('');
  const [lastWorkflowActor, setLastWorkflowActor] = useState<string>('');
  const [copyFromPRId, setCopyFromPRId] = useState<number | null>(null);
  const [directPostToSAPTest, setDirectPostToSAPTest] = useState(false);

  const [projectOptions, setProjectOptions] = useState<any[]>([]);
  const [costCenterOptions, setCostCenterOptions] = useState<any[]>([]);
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);
  const [paymentTermOptions, setPaymentTermOptions] = useState<any[]>([]);
  const [itemOptions, setItemOptions] = useState<any[]>([]);
  const [availableApprovalFlows, setAvailableApprovalFlows] = useState<any[]>([]);
  const [selectedApprovalFlow, setSelectedApprovalFlow] = useState<string>('');
  const [loadingApprovalFlows, setLoadingApprovalFlows] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);

  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [vendorPickerOpen, setVendorPickerOpen] = useState(false);
  const [itemPickerOpen, setItemPickerOpen] = useState(false);
  const [currentLineIndex, setCurrentLineIndex] = useState<number | null>(null);
  const [inlinePickerOpen, setInlinePickerOpen] = useState(false);
  const [inlinePickerType, setInlinePickerType] = useState<'warehouse' | 'project' | 'dim1' | 'dim2' | 'dim3' | 'dim4' | 'dim5'>('warehouse');
  const [inlineLineIndex, setInlineLineIndex] = useState<number | null>(null);
  
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [itemSearchLoading, setItemSearchLoading] = useState(false);
  const [inlineQuery, setInlineQuery] = useState('');
  const [inlineRows, setInlineRows] = useState<any[]>([]);
  const [inlineLoading, setInlineLoading] = useState(false);
  const [chargesDialogOpen, setChargesDialogOpen] = useState(false);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [taxDialogOpen, setTaxDialogOpen] = useState(false);
  const [chargeRows, setChargeRows] = useState<ChargeRow[]>([
    { type: 'OTHERS ESTIMATED CHARGES (Finance, Insurance, Installation)', amount: 0 },
    { type: 'FREIGHT CHARGES', amount: 0 },
    { type: 'CUSTOMS CHARGES', amount: 0 },
    { type: 'DELIVERY ORDER', amount: 0 },
    { type: 'LOCAL CLEARANCE CHARGES', amount: 0 },
  ]);
  // Which charge types the company sends to SAP as freight (Company Management; utils/poCharges).
  const [chargeCodes, setChargeCodes] = useState<ChargeExpenseCodes>({});
  const headerDiscountModeRef = useRef<'pct' | 'amt'>('pct');
  const taxModeRef = useRef<'pct' | 'amt'>('pct');
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [poSearchOpen, setPoSearchOpen] = useState(false);
  const [poSearchQuery, setPoSearchQuery] = useState('');
  const [myPOs, setMyPOs] = useState<any[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('po_grid_view');
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      itemcode: true,
      itemdescription: true,
      quantity: true,
      openqty: true,
      closedqty: true,
      uomcode: true,
      unitprice: true,
      discpct: true,
      discamt: true,
      linetotal: true,
      accountcode: true,
      warehouse: true,
      deliverydate: true,
      projectcode: true,
      dim1: true,
      dim2: true,
      dim3: true,
      dim4: true,
      dim5: true,
      lineremarks: true,
    };
  });
  const [gridDensity, setGridDensity] = useState<'compact' | 'standard' | 'comfortable'>(() => {
    try {
      return (localStorage.getItem('po_grid_density') as any) || 'compact';
    } catch {
      return 'compact';
    }
  });
  const [gridMenuAnchor, setGridMenuAnchor] = useState<null | HTMLElement>(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [densityOpen, setDensityOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; rowIndex: number | null } | null>(null);
  const [clipboardRow, setClipboardRow] = useState<POLine | null>(null);
  const [clipboardMode, setClipboardMode] = useState<'copy' | 'cut' | null>(null);
  const [gridFontSize, setGridFontSize] = useState<string>('12px');
  const [centerPopup, setCenterPopup] = useState<{
    open: boolean;
    title: string;
    message: string;
    severity: 'success' | 'error';
    details: string[];
  }>({
    open: false,
    title: '',
    message: '',
    severity: 'success',
    details: [],
  });
  const [postSubmitNavigateId, setPostSubmitNavigateId] = useState<number | null>(null);
  const currencyOptions = useMemo(
    () => ['SAR', 'AED', 'QAR', 'KWD', 'BHD', 'OMR', 'USD', 'EUR', 'CNY'],
    []
  );
  const baseChargeRows = useMemo<ChargeRow[]>(
    () => [
      { type: 'OTHERS ESTIMATED CHARGES (Finance, Insurance, Installation)', amount: 0 },
      { type: 'FREIGHT CHARGES', amount: 0 },
      { type: 'CUSTOMS CHARGES', amount: 0 },
      { type: 'DELIVERY ORDER', amount: 0 },
      { type: 'LOCAL CLEARANCE CHARGES', amount: 0 },
    ],
    []
  );
  const canEditCurrentPO = useMemo(() => {
    if (!createdPoId) return true;
    const status = String(poStatus || '').trim().toLowerCase();
    return status === 'draft' || status === 'rejected';
  }, [createdPoId, poStatus]);
  const isReworkedPO = useMemo(
    () => canEditCurrentPO && String(lastWorkflowAction || '').toLowerCase() === 'returned',
    [canEditCurrentPO, lastWorkflowAction]
  );
  const canUseDirectPostToSAPTest = requisitionType === 'Service';
  const actionButtonsDisabled = submitting || !canEditCurrentPO || !!postSubmitNavigateId;

  useEffect(() => {
    if (!canUseDirectPostToSAPTest && directPostToSAPTest) {
      setDirectPostToSAPTest(false);
    }
  }, [canUseDirectPostToSAPTest, directPostToSAPTest]);

  const rowHeight = useMemo(() => {
    if (gridDensity === 'compact') return 28;
    if (gridDensity === 'comfortable') return 42;
    return 34;
  }, [gridDensity]);
  const headerHeight = useMemo(() => {
    if (gridDensity === 'compact') return 30;
    if (gridDensity === 'comfortable') return 46;
    return 36;
  }, [gridDensity]);

  useEffect(() => {
    const companyId = Number(localStorage.getItem('selectedCompanyId') || '') || 0;
    if (!companyId) return;
    apiService.getPOChargeCodes(companyId)
      .then((res: any) => setChargeCodes(readChargeExpenseCodes(res?.data?.codes)))
      .catch(() => setChargeCodes({}));
  }, []);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
        const [projRes, ccRes, vendorRes, payTermsRes] = await Promise.all([
          apiService.get('/sap/projects', { companyId: selectedCompanyId }),
          apiService.get('/master-data/distribution-rules', { companyid: selectedCompanyId, dimension: 2 }),
          apiService.get('/sap/vendors', { companyId: selectedCompanyId }),
          apiService.get('/sap/payment-terms-types', { companyId: selectedCompanyId }),
        ]);
        setProjectOptions((projRes as any)?.data || projRes || []);
        setCostCenterOptions((ccRes as any)?.data || ccRes || []);
        setVendorOptions((vendorRes as any)?.data || vendorRes || []);
        setPaymentTermOptions((payTermsRes as any)?.data || payTermsRes || []);
      } catch (error) {
        console.error('Error loading options:', error);
      }
    };
    loadOptions();
  }, []);

  useEffect(() => {
    const loadMyPOs = async () => {
      try {
        const res = await apiService.getMyPOs();
        setMyPOs((res as any)?.data || res || []);
      } catch {
        setMyPOs([]);
      }
    };
    void loadMyPOs();
  }, []);

  useEffect(() => {
    try { localStorage.setItem('po_grid_view', JSON.stringify(columnVisibilityModel)); } catch {}
  }, [columnVisibilityModel]);

  useEffect(() => {
    try { localStorage.setItem('po_grid_density', gridDensity); } catch {}
    setGridFontSize(gridDensity === 'compact' ? '12px' : gridDensity === 'comfortable' ? '14px' : '13px');
  }, [gridDensity]);

  useEffect(() => {
    if (!lines.some((line) => Number(line.sourceprid || 0) > 0)) {
      setCopyFromPRId(null);
    }
  }, [lines]);

  useEffect(() => {
    if (!paymentTermCode) {
      setPaymentTermName('');
      return;
    }
    const match = paymentTermOptions.find((row: any) => String(row.GroupNumber || row.groupnumber || '').trim() === String(paymentTermCode).trim());
    if (match) {
      setPaymentTermName(String(match.PaymentTermsGroupName || match.paymenttermsgroupname || match.Name || ''));
    }
  }, [paymentTermCode, paymentTermOptions]);

  useEffect(() => {
    if (allocationCode && allocationType === 'Project' && projectOptions.length > 0) {
      const proj = projectOptions.find((p: any) => (p.Code || p.code) === allocationCode);
      if (proj) {
        setAllocationName(proj.Name || proj.name || '');
      }
    } else if (allocationCode && allocationType === 'CostCenter' && costCenterOptions.length > 0) {
      const cc = costCenterOptions.find((c: any) => (c.FactorCode || c.factorcode) === allocationCode);
      if (cc) {
        setAllocationName(cc.FactorDescription || cc.factordescription || '');
      }
    }
  }, [allocationCode, allocationType, projectOptions, costCenterOptions]);

  useEffect(() => {
    const loadMatchingApprovalFlows = async () => {
      const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
      const currentUserId = Number((user as any)?.userid || 0);
      if (!selectedCompanyId || !currentUserId || !allocationCode) {
        setAvailableApprovalFlows([]);
        setSelectedApprovalFlow('');
        return;
      }
      setLoadingApprovalFlows(true);
      try {
        const res = await apiService.listApprovalWorkflows({
          documentType: 'PO',
          requisitionType,
          allocationType,
          companyId: selectedCompanyId
        });
        const rows = (res as any)?.data || res || [];
        const flowMap = new Map<string, any>();
        for (const row of rows) {
          const flowName = String(row.flowname || '').trim();
          if (!flowName) continue;
          const key = `${flowName}|${row.companyid ?? 'null'}`;
          if (!flowMap.has(key)) {
            flowMap.set(key, {
              flowname: flowName,
              requisitiontype: row.requisitiontype,
              allocationtype: row.allocationtype,
              allocationcode: row.allocationcode,
              facilitytype: row.facilitytype,
              cycle: row.cycle,
              companyid: row.companyid ?? null
            });
          }
        }
        const uniqueFlows = Array.from(flowMap.values());
        const filtered = uniqueFlows.filter((flow: any) => {
          const reqTypeMatch = String(flow.requisitiontype || '').toUpperCase() === String(requisitionType || '').toUpperCase();
          const allocTypeMatch = String(flow.allocationtype || '').toUpperCase() === String(allocationType || '').toUpperCase();
          const flowAllocCodes = String(flow.allocationcode || '')
            .split(',')
            .map((code) => code.trim().toUpperCase())
            .filter(Boolean);
          const poAllocCode = String(allocationCode || '').trim().toUpperCase();
          const allocCodeMatch = flowAllocCodes.length === 0 || flowAllocCodes.includes(poAllocCode);
          const flowFacility = String(flow.facilitytype || '').trim().toUpperCase();
          const poFacility = String(facilityType || '').trim().toUpperCase();
          const facilityMatch = !flowFacility || flowFacility === poFacility;
          const flowCycle = String(flow.cycle || '').trim().toUpperCase();
          const poCycle = String(cycle || '').trim().toUpperCase();
          const cycleMatch = !flowCycle || flowCycle === poCycle;
          const companyMatch = String(flow.companyid ?? '') === String(selectedCompanyId ?? '');
          return reqTypeMatch && allocTypeMatch && allocCodeMatch && facilityMatch && cycleMatch && companyMatch;
        });
        const initiatorMatched: any[] = [];
        for (const flow of filtered) {
          const initRes = await apiService.listApprovalInitiators({
            documenttype: 'PO',
            requisitiontype: requisitionType,
            allocationtype: allocationType,
            flowname: flow.flowname,
            companyid: selectedCompanyId
          });
          const initiators = (initRes as any)?.data || initRes || [];
          const initiatorIds = initiators.map((i: any) => Number(i.userid || i.UserID || 0));
          if (initiatorIds.length === 0 || initiatorIds.includes(currentUserId)) {
            initiatorMatched.push(flow);
          }
        }
        setAvailableApprovalFlows(initiatorMatched);
        if (initiatorMatched.length === 1) {
          setSelectedApprovalFlow(String(initiatorMatched[0].flowname || ''));
        } else {
          setSelectedApprovalFlow((prev) =>
            initiatorMatched.some((f: any) => String(f.flowname || '') === String(prev || '')) ? prev : ''
          );
        }
      } catch {
        setAvailableApprovalFlows([]);
        setSelectedApprovalFlow('');
      } finally {
        setLoadingApprovalFlows(false);
      }
    };
    void loadMatchingApprovalFlows();
  }, [user, requisitionType, allocationType, allocationCode, facilityType, cycle]);

  const refreshAttachments = useCallback(async () => {
    if (!createdPoId) return;
    try {
      const res = await apiService.getPOAttachments(createdPoId);
      const data = (res as any)?.data || res || [];
      setAttachments(Array.isArray(data) ? data : []);
    } catch {
      setAttachments([]);
    }
  }, [createdPoId]);

  useEffect(() => {
    if (!createdPoId) {
      setAttachments([]);
      return;
    }
    void refreshAttachments();
  }, [createdPoId, refreshAttachments]);

  const uploadAttachments = async (files: FileList | null) => {
    if (!createdPoId || !files || files.length === 0 || !canEditCurrentPO) return;
    setUploadingAttachments(true);
    try {
      await apiService.uploadPOAttachments(createdPoId, Array.from(files));
      await refreshAttachments();
      dispatch(showSnackbar({ message: 'Attachment(s) uploaded', severity: 'success' }));
    } catch {
      dispatch(showSnackbar({ message: 'Failed to upload attachment(s)', severity: 'error' }));
    } finally {
      setUploadingAttachments(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  };

  const removeAttachment = async (attachmentId: number) => {
    if (!createdPoId || !canEditCurrentPO) return;
    try {
      await apiService.deletePOAttachment(createdPoId, attachmentId);
      await refreshAttachments();
      dispatch(showSnackbar({ message: 'Attachment deleted', severity: 'success' }));
    } catch {
      dispatch(showSnackbar({ message: 'Failed to delete attachment', severity: 'error' }));
    }
  };

  useEffect(() => {
    setLines((prev) => prev.map((line) => ({
      ...line,
      itemtype: requisitionType,
      projectcode: allocationType === 'Project' ? (line.projectcode || allocationCode || '') : '',
      dim2: allocationType === 'CostCenter' ? (allocationCode || line.dim2 || '') : line.dim2,
    })));
  }, [requisitionType, allocationType, allocationCode]);

  const filteredProjectOptions = useMemo(() => {
    const opts = allocationType === 'Project' ? projectOptions : costCenterOptions;
    if (!projectSearchQuery.trim()) return opts;
    const q = projectSearchQuery.toLowerCase();
    return opts.filter((o: any) => {
      if (allocationType === 'Project') {
        return (o.Code || o.code || '').toLowerCase().includes(q) ||
               (o.Name || o.name || '').toLowerCase().includes(q);
      } else {
        return (o.FactorCode || o.factorcode || '').toLowerCase().includes(q) ||
               (o.FactorDescription || o.factordescription || '').toLowerCase().includes(q);
      }
    });
  }, [projectOptions, costCenterOptions, projectSearchQuery, allocationType]);

  const filteredVendorOptions = useMemo(() => {
    if (!vendorSearchQuery.trim()) return vendorOptions;
    const q = vendorSearchQuery.toLowerCase();
    return vendorOptions.filter((v: any) =>
      (v.CardCode || v.cardcode || '').toLowerCase().includes(q) ||
      (v.CardName || v.cardname || '').toLowerCase().includes(q)
    );
  }, [vendorOptions, vendorSearchQuery]);

  const filteredMyPOs = useMemo(() => {
    if (!poSearchQuery.trim()) return myPOs;
    const q = poSearchQuery.toLowerCase();
    return myPOs.filter((po: any) =>
      String(po.ponumber || po.PONumber || '').toLowerCase().includes(q) ||
      String(po.vendorname || po.VendorName || '').toLowerCase().includes(q) ||
      String(po.allocationcode || po.AllocationCode || '').toLowerCase().includes(q) ||
      String(po.status || po.Status || '').toLowerCase().includes(q)
    );
  }, [myPOs, poSearchQuery]);

  const searchItems = useCallback(async (query: string) => {
    setItemSearchLoading(true);
    try {
      const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
      const res = await apiService.get('/sap/items/custom', {
        companyId: selectedCompanyId,
        requisitionType,
        filter: query.trim() || undefined,
        top: 200
      });
      setItemOptions((res as any)?.data || []);
    } catch (error) {
      setItemOptions([]);
    } finally {
      setItemSearchLoading(false);
    }
  }, [requisitionType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (itemPickerOpen) {
        void searchItems(itemSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [itemSearchQuery, itemPickerOpen, searchItems]);

  useEffect(() => {
    const loadInlineRows = async () => {
      if (!inlinePickerOpen) return;
      setInlineLoading(true);
      try {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
        if (inlinePickerType === 'warehouse') {
          const res = await apiService.get('/sap/warehouses', {
            companyId: selectedCompanyId,
          });
          const all = (res as any)?.data || res || [];
          const q = String(inlineQuery || '').toLowerCase().trim();
          setInlineRows(q ? all.filter((r: any) => String(r.WarehouseCode || '').toLowerCase().includes(q) || String(r.WarehouseName || '').toLowerCase().includes(q)) : all);
        } else if (inlinePickerType === 'project') {
          const res = await apiService.get('/sap/projects', {
            companyId: selectedCompanyId,
          });
          const all = (res as any)?.data || res || [];
          const q = String(inlineQuery || '').toLowerCase().trim();
          setInlineRows(q ? all.filter((r: any) => String(r.Code || '').toLowerCase().includes(q) || String(r.Name || '').toLowerCase().includes(q)) : all);
        } else {
          // Keep SAP dimension mapping aligned with PR page:
          // dim4 (Auxiliary) -> dimension 5, dim5 (LOC) -> dimension 4
          const dimension = inlinePickerType === 'dim1'
            ? 1
            : inlinePickerType === 'dim2'
              ? 2
              : inlinePickerType === 'dim3'
                ? 3
                : inlinePickerType === 'dim4'
                  ? 5
                  : 4;
          const res = await apiService.get('/master-data/distribution-rules', {
            companyid: selectedCompanyId,
            dimension,
          });
          const all = (res as any)?.data || res || [];
          const q = String(inlineQuery || '').toLowerCase().trim();
          setInlineRows(q ? all.filter((r: any) => String(r.FactorCode || '').toLowerCase().includes(q) || String(r.FactorDescription || '').toLowerCase().includes(q)) : all);
        }
      } catch {
        setInlineRows([]);
      } finally {
        setInlineLoading(false);
      }
    };
    const timer = setTimeout(() => { void loadInlineRows(); }, 250);
    return () => clearTimeout(timer);
  }, [inlinePickerOpen, inlinePickerType, inlineQuery]);

  const createEmptyLine = (): POLine => ({
    itemtype: requisitionType,
    itemdescription: '',
    quantity: 1,
    openqty: 0,
    closedqty: 0,
    unitprice: 0,
    accountcode: '',
    warehouse: '',
    deliverydate: '',
    projectcode: allocationType === 'Project' ? allocationCode : '',
    dim1: '',
    dim2: allocationType === 'CostCenter' ? allocationCode : '',
    dim3: '',
    dim4: '',
    dim5: '',
    lineremarks: '',
    discpct: 0,
    discamt: 0,
    sourceprid: undefined,
    sourceprlinenum: undefined
  });

  const addLine = () => {
    setLines([...lines, createEmptyLine()]);
  };

  const removeSelectedLines = () => {
    if (!canEditCurrentPO) return;
    const selectedRows = lineGridRef.current?.api?.getSelectedRows?.() || [];
    if (!selectedRows.length) return;
    const selectedSet = new Set(selectedRows.map((r: POLine) => lines.indexOf(r)));
    const remaining = lines.filter((_, idx) => !selectedSet.has(idx));
    if (remaining.length === 0) {
      setLines([createEmptyLine()]);
      return;
    }
    setLines(remaining);
  };

  const updateLine = (index: number, patch: Partial<POLine>) => {
    if (!canEditCurrentPO) return;
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], ...patch };
    setLines(newLines);
  };

  const getProjectDrivenLineDefaults = useCallback(async (projectCode: string) => {
    const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
    const normalizedProjectCode = String(projectCode || '').trim();
    if (!selectedCompanyId || !normalizedProjectCode) {
      return {} as Partial<POLine>;
    }

    try {
      const res = await apiService.get<any>('/master-data', {
        companyid: selectedCompanyId,
        endpointkey: 'sap-projects',
        keycode: normalizedProjectCode
      });
      const projectData = ((res.data || [])[0] || {}).jsondata || {};

      let auxiliary = String(projectData.U_SCOPE ?? '').trim();
      if (!auxiliary) {
        try {
          const auxRes = await apiService.get<any>('/master-data/auxiliary-by-project', {
            projectcode: normalizedProjectCode,
            companyid: selectedCompanyId
          });
          const auxList = (auxRes as any)?.data || auxRes || [];
          if (auxList.length === 1) {
            const item = auxList[0];
            const factorData = item.data || item;
            auxiliary = String(factorData.FactorCode || factorData.Code || factorData.DistributionRule || '').trim();
          }
        } catch {}
      }

      return {
        warehouse: String(projectData.U_WH ?? '').trim() || undefined,
        dim1: String(projectData.U_Division ?? '').trim() || undefined,
        dim2: String(projectData.U_Department ?? '').trim() || undefined,
        dim4: auxiliary || undefined,
        dim5: String(projectData.U_LOC ?? '').trim() || undefined,
      } as Partial<POLine>;
    } catch {
      return {} as Partial<POLine>;
    }
  }, []);

  const getItemDrivenLineDefaults = useCallback(async (item: any) => {
    const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
    const itemCode = String(item?.ItemCode || item?.itemcode || '').trim();
    const itemsGroupCode = String(item?.ItemsGroupCode ?? item?.data?.ItemsGroupCode ?? '').trim();
    const updates: Partial<POLine> = {};

    if (!selectedCompanyId || !itemCode) {
      return updates;
    }

    try {
      // The Auxiliary is the item's own (SAP U_AUX_Code): the server reads SAP when its synced item lacks it.
      const [itemRes, auxiliaries] = await Promise.all([
        apiService.get<any>('/master-data', {
          companyid: selectedCompanyId,
          endpointkey: 'sap-items',
          keycode: itemCode
        }),
        apiService.getItemAuxiliaries([itemCode], selectedCompanyId).catch(() => ({} as Record<string, string | null>)),
      ]);
      const itemData = ((itemRes.data || [])[0] || {}).jsondata || {};
      const auxCode = String(auxiliaries[itemCode] || '').trim();
      const unitPrice = Number(
        itemData.ItemPrices?.[0]?.Price ||
        itemData.LastPurchasePrice ||
        itemData.AvgStdPrice ||
        itemData.PurchasePrice ||
        0
      );
      if (auxCode) updates.dim4 = auxCode;
      if (unitPrice > 0) updates.unitprice = unitPrice;
    } catch {}

    if (itemsGroupCode) {
      try {
        const gRes = await apiService.getItemGroups({ companyId: selectedCompanyId, code: itemsGroupCode });
        const arr = (gRes as any)?.data || gRes || [];
        const grp = arr[0];
        const uCost = String((grp?.Data?.U_MCSR_CODE ?? grp?.U_MCSR_CODE ?? '') || '').trim();
        if (uCost) updates.dim3 = uCost;
      } catch {}
    }

    return updates;
  }, []);

  const handleItemSelect = async (item: any) => {
    const basePatch: Partial<POLine> = {
      itemcode: item.ItemCode || item.itemcode || '',
      itemdescription: item.ItemName || item.itemname || '',
      uomcode: item.PurchaseUnit || item.purchaseunit || '',
      unitprice: item.Price || item.price || 0
    };
    const itemDefaults = await getItemDrivenLineDefaults(item);
    const mergedPatch = { ...basePatch, ...itemDefaults };

    if (currentLineIndex !== null) {
      updateLine(currentLineIndex, mergedPatch);
    } else {
      setLines([...lines, {
        ...createEmptyLine(),
        ...mergedPatch,
      }]);
    }
    setItemPickerOpen(false);
    setCurrentLineIndex(null);
    setItemSearchQuery('');
    setItemOptions([]);
  };

  useEffect(() => {
    if (!allocationCode || requisitionType !== 'Subcontracting') {
      return;
    }

    if (allocationType === 'Project') {
      void (async () => {
        const projectDefaults = await getProjectDrivenLineDefaults(allocationCode);
        setLines((ls) => ls.map((line) => ({
          ...line,
          projectcode: allocationCode,
          dim1: projectDefaults.dim1 || line.dim1 || '',
          dim2: projectDefaults.dim2 || line.dim2 || '',
          dim4: projectDefaults.dim4 || line.dim4 || '',
          dim5: projectDefaults.dim5 || line.dim5 || '',
          warehouse: projectDefaults.warehouse || line.warehouse || '',
        })));
      })();
      return;
    }

    if (allocationType === 'CostCenter') {
      setLines((ls) => ls.map((line) => ({
        ...line,
        dim2: allocationCode,
      })));
    }
  }, [allocationCode, allocationType, requisitionType, getProjectDrivenLineDefaults]);

  const lineGrossTotal = useMemo(
    () => lines.reduce((sum, line) => sum + ((Number(line.quantity) || 0) * (Number(line.unitprice) || 0)), 0),
    [lines]
  );
  const lineDiscountTotal = useMemo(
    () => lines.reduce((sum, line) => sum + (Number(line.discamt) || 0), 0),
    [lines]
  );
  const chargeTotal = useMemo(
    () => chargeRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [chargeRows]
  );
  const netBeforeHeaderDiscount = useMemo(
    () => Math.max(0, lineGrossTotal - lineDiscountTotal),
    [lineGrossTotal, lineDiscountTotal]
  );
  const netAfterHeaderDiscount = useMemo(
    () => Math.max(0, netBeforeHeaderDiscount - (Number(headerDiscAmt) || 0)),
    [netBeforeHeaderDiscount, headerDiscAmt]
  );
  const grandTotal = useMemo(
    () => Math.max(0, netAfterHeaderDiscount + (Number(taxAmt) || 0) + chargeTotal),
    [netAfterHeaderDiscount, taxAmt, chargeTotal]
  );
  // What the SAP PO carries before SAP's VAT: the lines after the header discount and the charges sent as
  // freight. The tax and the other charges stay PRPO estimates (backend utils/poHeaderTotals).
  const sapAmountBeforeVat = useMemo(
    () => netAfterHeaderDiscount + chargeRows.reduce((sum, row) => sum + (sapExpenseFor(chargeCodes, row.type) ? (Number(row.amount) || 0) : 0), 0),
    [netAfterHeaderDiscount, chargeRows, chargeCodes]
  );
  const showSapAmount = Math.abs(sapAmountBeforeVat - grandTotal) >= 0.005;

  useEffect(() => {
    if (headerDiscountModeRef.current === 'pct') {
      const amt = (netBeforeHeaderDiscount * (Number(headerDiscPct) || 0)) / 100;
      setHeaderDiscAmt(Number(amt.toFixed(2)));
    } else {
      const pct = netBeforeHeaderDiscount > 0 ? ((Number(headerDiscAmt) || 0) * 100) / netBeforeHeaderDiscount : 0;
      setHeaderDiscPct(Number(pct.toFixed(4)));
    }
  }, [netBeforeHeaderDiscount, headerDiscPct, headerDiscAmt]);

  useEffect(() => {
    if (taxModeRef.current === 'pct') {
      const amt = (netAfterHeaderDiscount * (Number(taxPct) || 0)) / 100;
      setTaxAmt(Number(amt.toFixed(2)));
    } else {
      const pct = netAfterHeaderDiscount > 0 ? ((Number(taxAmt) || 0) * 100) / netAfterHeaderDiscount : 0;
      setTaxPct(Number(pct.toFixed(4)));
    }
  }, [netAfterHeaderDiscount, taxPct, taxAmt]);

  // The checks Save as Draft and Submit make before sending; the summary panel lists the Submit ones.
  const collectPOValidationErrors = (asDraft: boolean): string[] => {
    const validationErrors: string[] = [];
    if (!allocationCode) validationErrors.push('Project/Cost Center is required.');
    if (!vendorCode) validationErrors.push('Vendor is required.');
    if (!postingDate) validationErrors.push('Posting Date is required.');
    if (!dueDate) validationErrors.push('Due Date is required.');
    // A 2-digit year typed in a date box arrives as 00yy (read as 20yy); other years SAP cannot hold.
    ([['Posting Date', postingDate], ['Due Date', dueDate], ['Document Date', documentDate]] as const).forEach(([label, value]) => {
      if (value && !isDocumentDate(fixTwoDigitYear(value))) validationErrors.push(`${label} ${value} is not a valid date: enter the year with 4 digits.`);
    });
    if (!asDraft && requisitionType !== 'Subcontracting' && !selectedApprovalFlow) {
      validationErrors.push('Approval Flow is required.');
    }
    lines.forEach((line, idx) => {
      const rowErrors: string[] = [];
      if (!String(line.itemdescription || '').trim()) rowErrors.push('Description is required');
      if ((Number(line.quantity) || 0) <= 0) rowErrors.push('Quantity must be greater than 0');
      if (line.deliverydate && !isDocumentDate(fixTwoDigitYear(line.deliverydate))) rowErrors.push('Delivery date needs a 4-digit year');
      if ((Number(line.sourceprid) || 0) > 0 && (Number(line.quantity) || 0) > (Number(line.openqty) || 0)) {
        rowErrors.push(`Quantity cannot exceed open qty ${(Number(line.openqty) || 0).toFixed(4)}`);
      }
      if (rowErrors.length) validationErrors.push(`Line ${idx + 1}: ${rowErrors.join(', ')}`);
    });
    return validationErrors;
  };

  const handleSubmit = async (asDraft: boolean = false) => {
    if (!canEditCurrentPO) {
      setCenterPopup({
        open: true,
        title: 'PO Not Editable',
        message: 'Only draft or rejected POs can be changed from this page.',
        severity: 'error',
        details: [],
      });
      return;
    }
    const validationErrors = collectPOValidationErrors(asDraft);
    if (validationErrors.length > 0) {
      setCenterPopup({
        open: true,
        title: 'Validation Error',
        message: 'Please correct the following fields before submission.',
        severity: 'error',
        details: validationErrors,
      });
      return;
    }

    setSubmitting(true);
    try {
      const getChargeAmount = (chargeType: ChargeRow['type']) =>
        Number(chargeRows.find((r) => r.type === chargeType)?.amount) || 0;
      const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
      const useDirectPostToSAPTest = !asDraft && (requisitionType === 'Subcontracting' || (canUseDirectPostToSAPTest && directPostToSAPTest));
      const poData = {
        requisitiontype: requisitionType,
        allocationtype: allocationType,
        allocationcode: allocationCode,
        facilitytype: facilityType,
        cycle: cycle,
        postingdate: postingDate,
        requireddate: dueDate,
        documentdate: documentDate,
        remarks: remarks,
        vendorcode: vendorCode,
        vendorname: vendorName,
        paymenttermcode: paymentTermCode || undefined,
        paymenttermname: paymentTermName || undefined,
        deliverytype: deliveryType || undefined,
        incoterms: incoterms || undefined,
        modeofshipment: modeOfShipment || undefined,
        requireddocuments: requiredDocuments || undefined,
        consignee: consignee || undefined,
        shipmentrequirements: shipmentRequirements || undefined,
        currency: currency || 'SAR',
        sellingprice: Number(sellingPrice) || 0,
        headerdiscpct: Number(headerDiscPct) || 0,
        headerdiscamt: Number(headerDiscAmt) || 0,
        taxpct: Number(taxPct) || 0,
        taxamt: Number(taxAmt) || 0,
        customduty: getChargeAmount('CUSTOMS CHARGES'),
        freight: getChargeAmount('FREIGHT CHARGES'),
        othercharges: getChargeAmount('OTHERS ESTIMATED CHARGES (Finance, Insurance, Installation)'),
        chargetotal: Number(chargeTotal) || 0,
        grandtotal: Number(grandTotal) || 0,
        totalestimatedpocost: Number(grandTotal) || 0,
        additionalcharges: chargeRows.map((r) => ({ type: String(r.type || ''), amount: Number(r.amount) || 0 })),
        approvalflowname: selectedApprovalFlow || undefined,
        copyfromprid: copyFromPRId || undefined,
        companyid: selectedCompanyId,
        directposttosaptest: useDirectPostToSAPTest,
        lines: lines.map((l, i) => ({
          ...l,
          deliverydate: (String(l.deliverydate || '').trim() ? fixTwoDigitYear(l.deliverydate) : undefined),
          linenum: i + 1,
          linetotal: Math.max(0, (Number(l.quantity) || 0) * (Number(l.unitprice) || 0) - (Number(l.discamt) || 0))
        }))
      };

      let response: any;
      if (createdPoId) {
        await apiService.updatePODraft(createdPoId, poData);
        response = asDraft
          ? await apiService.getPO(createdPoId)
          : await apiService.submitPODraft(createdPoId, { directposttosaptest: useDirectPostToSAPTest });
      } else {
        response = asDraft
          ? await apiService.createPODraft(poData)
          : await apiService.createPO(poData);
      }

      const data = (response as any)?.data || response;
      const currentPoId = Number(data.poid || createdPoId || 0);
      setCreatedPoId(currentPoId || null);
      setPoNumber(String(data.ponumber || poNumber || ''));
      setPoStatus(String(data.status || (asDraft ? poStatus || 'Draft' : 'Pending')));
      if (!asDraft) {
        setLastWorkflowAction('');
        setLastWorkflowComment('');
        setLastWorkflowActor('');
      }
      const submission = (data as any)?.submission || {};
      const stageName = String(submission?.stageName || '').trim();
      const approverNames = Array.isArray(submission?.approverNames) ? submission.approverNames.filter(Boolean) : [];
      const successDetails: string[] = [];
      if (!asDraft) {
        successDetails.push(`PO Number: ${String(submission?.poNumber || data.ponumber || poNumber || '')}`);
        if (useDirectPostToSAPTest) {
          if (data.sappodocnum || data.sappodocentry) {
            successDetails.push(`SAP PO: DocNum ${String(data.sappodocnum || '')} / DocEntry ${String(data.sappodocentry || '')}`);
          }
          successDetails.push(requisitionType === 'Subcontracting'
            ? 'Approval flow bypassed for subcontracting PO.'
            : 'Approval flow bypassed for test posting.');
        } else {
          if (stageName) successDetails.push(`Stage: ${stageName}`);
          if (approverNames.length) successDetails.push(`Sent to: ${approverNames.join(', ')}`);
        }
      }

      setCenterPopup({
        open: true,
        title: 'Success',
        message: asDraft
          ? (createdPoId ? 'PO draft updated successfully.' : 'PO draft saved successfully.')
          : useDirectPostToSAPTest
            ? (requisitionType === 'Subcontracting'
                ? (createdPoId ? 'Subcontracting PO posted directly to SAP.' : 'Subcontracting PO created and posted directly to SAP.')
                : (createdPoId ? 'PO posted directly to SAP for testing.' : 'PO created and posted directly to SAP for testing.'))
            : (createdPoId ? 'PO submitted for approval.' : 'PO created and submitted for approval.'),
        severity: 'success',
        details: successDetails,
      });
      if (!asDraft) {
        setPostSubmitNavigateId(currentPoId);
      } else {
        setPostSubmitNavigateId(null);
        if (currentPoId) {
          await loadPOById(currentPoId, { silent: true });
        }
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || 'Failed to create PO';
      const apiErrors = Array.isArray(error?.response?.data?.errors) ? error.response.data.errors : [];
      const details = apiErrors.map((e: any) => {
        const field = String(e?.field || '').trim();
        const message = String(e?.message || '').trim();
        return field ? `${field}: ${message}` : message;
      }).filter(Boolean);
      setCenterPopup({
        open: true,
        title: 'Submission Failed',
        message: msg,
        severity: 'error',
        details,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    navigate('/dashboard');
  };

  const handleVendorSelect = (vendor: any) => {
    const vendorCurrency = String(
      vendor.Currency ||
      vendor.currency ||
      vendor.CurrCode ||
      vendor.currcode ||
      vendor.Data?.Currency ||
      vendor.Data?.CurrencyCode ||
      vendor.Data?.CurrCode ||
      ''
    ).toUpperCase();
    const nextCurrency = currencyOptions.includes(vendorCurrency) ? vendorCurrency : 'SAR';
    setVendorCode(vendor.CardCode || vendor.cardcode || '');
    setVendorName(vendor.CardName || vendor.cardname || '');
    setCurrency(nextCurrency);
    setVendorPickerOpen(false);
    setVendorSearchQuery('');
  };

  const handleAllocationSelect = (opt: any) => {
    if (allocationType === 'Project') {
      setAllocationCode(opt.Code || opt.code || '');
      setAllocationName(opt.Name || opt.name || '');
    } else {
      setAllocationCode(opt.FactorCode || opt.factorcode || '');
      setAllocationName(opt.FactorDescription || opt.factordescription || '');
    }
    setProjectPickerOpen(false);
    setProjectSearchQuery('');
  };

  const itemColumnDefs = useMemo(() => [
    { field: 'ItemCode', headerName: 'Item Code', width: 140 },
    { field: 'ItemName', headerName: 'Item Name', width: 300 },
    { field: 'PurchaseUnit', headerName: 'UoM', width: 80 },
    { field: 'Price', headerName: 'Price', width: 100 },
  ], []);

  const lineColumnDefs = useMemo(() => [
    { headerName: '#', valueGetter: (p: any) => (p.node?.rowIndex ?? 0) + 1, width: 60, editable: false, pinned: 'left' as const },
    { field: 'itemcode', headerName: 'Item Code', minWidth: 130, editable: canEditCurrentPO, hide: columnVisibilityModel.itemcode === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'itemdescription', headerName: 'Description', minWidth: 220, editable: canEditCurrentPO, hide: columnVisibilityModel.itemdescription === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'quantity', headerName: 'Qty', minWidth: 90, editable: canEditCurrentPO, hide: columnVisibilityModel.quantity === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'openqty', headerName: 'Open Qty', minWidth: 100, editable: false, hide: columnVisibilityModel.openqty === false, cellStyle: { fontSize: gridFontSize, fontWeight: 600, color: '#166534' } },
    { field: 'closedqty', headerName: 'Closed Qty', minWidth: 100, editable: false, hide: columnVisibilityModel.closedqty === false, cellStyle: { fontSize: gridFontSize, fontWeight: 600, color: '#92400e' } },
    { field: 'uomcode', headerName: 'UoM', minWidth: 90, editable: canEditCurrentPO, hide: columnVisibilityModel.uomcode === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'unitprice', headerName: 'Unit Price', minWidth: 110, editable: canEditCurrentPO, hide: columnVisibilityModel.unitprice === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'discpct', headerName: 'Disc %', minWidth: 90, editable: canEditCurrentPO, hide: columnVisibilityModel.discpct === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'discamt', headerName: 'Disc Amt', minWidth: 110, editable: canEditCurrentPO, hide: columnVisibilityModel.discamt === false, cellStyle: { fontSize: gridFontSize } },
    {
      field: 'linetotal',
      headerName: 'Line Total',
      minWidth: 120,
      editable: false,
      hide: columnVisibilityModel.linetotal === false,
      cellStyle: { fontSize: gridFontSize },
      // After the line's discount, as it is saved, printed and sent to SAP (the totals list the gross and the discounts).
      valueGetter: (p: any) => Math.max(0, (Number(p.data?.quantity) || 0) * (Number(p.data?.unitprice) || 0) - (Number(p.data?.discamt) || 0)).toFixed(2),
    },
    { field: 'accountcode', headerName: 'Account Code', minWidth: 130, editable: canEditCurrentPO, hide: columnVisibilityModel.accountcode === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'warehouse', headerName: 'Warehouse', minWidth: 120, editable: canEditCurrentPO, hide: columnVisibilityModel.warehouse === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'deliverydate', headerName: 'Delivery Date', minWidth: 130, editable: canEditCurrentPO, hide: columnVisibilityModel.deliverydate === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'projectcode', headerName: 'Project Code', minWidth: 130, editable: canEditCurrentPO, hide: columnVisibilityModel.projectcode === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'dim1', headerName: 'Division', minWidth: 110, editable: canEditCurrentPO, hide: columnVisibilityModel.dim1 === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'dim2', headerName: 'Department', minWidth: 130, editable: canEditCurrentPO, hide: columnVisibilityModel.dim2 === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'dim3', headerName: 'Cost Code (WBS)', minWidth: 150, editable: canEditCurrentPO, hide: columnVisibilityModel.dim3 === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'dim4', headerName: 'Auxiliary', minWidth: 120, editable: canEditCurrentPO, hide: columnVisibilityModel.dim4 === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'dim5', headerName: 'LOC', minWidth: 110, editable: canEditCurrentPO, hide: columnVisibilityModel.dim5 === false, cellStyle: { fontSize: gridFontSize } },
    { field: 'lineremarks', headerName: 'Line Remarks', minWidth: 200, editable: canEditCurrentPO, hide: columnVisibilityModel.lineremarks === false, cellStyle: { fontSize: gridFontSize } },
  ], [canEditCurrentPO, columnVisibilityModel, gridFontSize]);

  const onLineCellValueChanged = (params: any) => {
    if (!canEditCurrentPO) return;
    const idx = lines.indexOf(params.data as POLine);
    if (idx === undefined || idx === null || idx < 0) return;
    const field = params.colDef.field as keyof POLine;
    const val = params.newValue;
    if (field === 'quantity' || field === 'unitprice') {
      let qty = field === 'quantity' ? (Number(val) || 0) : (Number(lines[idx].quantity) || 0);
      const maxOpenQty = Number(lines[idx].openqty || 0);
      if (field === 'quantity' && Number(lines[idx].sourceprid || 0) > 0 && maxOpenQty > 0 && qty > maxOpenQty) {
        qty = maxOpenQty;
        dispatch(showSnackbar({ message: `Line ${idx + 1} quantity cannot exceed PR open qty ${maxOpenQty.toFixed(4)}`, severity: 'error' }));
      }
      const price = field === 'unitprice' ? (Number(val) || 0) : (Number(lines[idx].unitprice) || 0);
      const gross = qty * price;
      const pct = Number(lines[idx].discpct) || 0;
      const amt = Number(lines[idx].discamt) || 0;
      const recalcAmt = pct > 0 ? (gross * pct) / 100 : amt;
      updateLine(idx, { [field]: field === 'quantity' ? qty : (Number(val) || 0), discamt: Number(recalcAmt.toFixed(2)) } as Partial<POLine>);
      return;
    }
    if (field === 'discpct') {
      const pct = Number(val) || 0;
      const gross = (Number(lines[idx].quantity) || 0) * (Number(lines[idx].unitprice) || 0);
      const amt = (gross * pct) / 100;
      updateLine(idx, { discpct: pct, discamt: Number(amt.toFixed(2)) });
      return;
    }
    if (field === 'discamt') {
      const amt = Number(val) || 0;
      const gross = (Number(lines[idx].quantity) || 0) * (Number(lines[idx].unitprice) || 0);
      const pct = gross > 0 ? (amt * 100) / gross : 0;
      updateLine(idx, { discamt: amt, discpct: Number(pct.toFixed(4)) });
      return;
    }
    updateLine(idx, { [field]: val } as Partial<POLine>);
  };

  const onLineCellDoubleClicked = (params: any) => {
    if (!canEditCurrentPO) return;
    const idx = lines.indexOf(params.data as POLine);
    const field = String(params.colDef.field || '');
    if (idx === undefined || idx === null || idx < 0) return;
    if (field === 'itemcode') {
      setCurrentLineIndex(idx);
      setItemPickerOpen(true);
      return;
    }
    if (['warehouse', 'projectcode', 'dim1', 'dim2', 'dim3', 'dim4', 'dim5'].includes(field)) {
      const t = field === 'projectcode' ? 'project' : field;
      setInlinePickerType(t as any);
      setInlineLineIndex(idx);
      setInlineQuery('');
      setInlineRows([]);
      setInlinePickerOpen(true);
    }
  };

  const inlineColumnDefs = useMemo(() => {
    if (inlinePickerType === 'warehouse') return [
      { field: 'WarehouseCode', headerName: 'Code', flex: 1 },
      { field: 'WarehouseName', headerName: 'Name', flex: 2 },
    ];
    if (inlinePickerType === 'project') return [
      { field: 'Code', headerName: 'Code', flex: 1 },
      { field: 'Name', headerName: 'Name', flex: 2 },
    ];
    return [
      { field: 'FactorCode', headerName: 'Code', flex: 1 },
      { field: 'FactorDescription', headerName: 'Description', flex: 2 },
    ];
  }, [inlinePickerType]);

  const chooseInlineValue = (row: any) => {
    if (!canEditCurrentPO) return;
    if (inlineLineIndex === null) return;
    if (inlinePickerType === 'warehouse') updateLine(inlineLineIndex, { warehouse: row.WarehouseCode || row.warehousecode || '' });
    if (inlinePickerType === 'project') updateLine(inlineLineIndex, { projectcode: row.Code || row.code || '' });
    if (inlinePickerType === 'dim1') updateLine(inlineLineIndex, { dim1: row.FactorCode || row.Code || '' });
    if (inlinePickerType === 'dim2') updateLine(inlineLineIndex, { dim2: row.FactorCode || row.Code || '' });
    if (inlinePickerType === 'dim3') updateLine(inlineLineIndex, { dim3: row.FactorCode || row.Code || '' });
    if (inlinePickerType === 'dim4') updateLine(inlineLineIndex, { dim4: row.FactorCode || row.Code || '' });
    if (inlinePickerType === 'dim5') updateLine(inlineLineIndex, { dim5: row.FactorCode || row.Code || '' });
    setInlinePickerOpen(false);
  };

  const chargeColumnDefs = useMemo(() => [
    { field: 'type', headerName: 'Additional Items', editable: false, flex: 2 },
    { field: 'amount', headerName: 'Amount', editable: canEditCurrentPO, flex: 1 },
    { colId: 'destination', headerName: 'Goes to', editable: false, flex: 1.3, valueGetter: (p: any) => chargeDestination(chargeCodes, p.data?.type) },
  ], [canEditCurrentPO, chargeCodes]);
  const valuePairColumnDefs = useMemo(() => [
    { field: 'label', headerName: 'Field', editable: false, flex: 2 },
    { field: 'value', headerName: 'Value', editable: canEditCurrentPO, flex: 1 },
  ], [canEditCurrentPO]);
  const discountRows = useMemo(
    () => [
      { key: 'pct', label: 'Discount %', value: Number(headerDiscPct) || 0 },
      { key: 'amt', label: 'Discount Amount', value: Number(headerDiscAmt) || 0 },
    ],
    [headerDiscPct, headerDiscAmt]
  );
  const taxRows = useMemo(
    () => [
      { key: 'pct', label: 'Tax %', value: Number(taxPct) || 0 },
      { key: 'amt', label: 'Tax Amount', value: Number(taxAmt) || 0 },
    ],
    [taxPct, taxAmt]
  );

  const onChargeCellValueChanged = (params: any) => {
    if (!canEditCurrentPO) return;
    const idx = params.node?.rowIndex;
    if (idx === undefined || idx === null || idx < 0) return;
    const next = [...chargeRows];
    next[idx] = { ...next[idx], amount: Number(params.newValue) || 0 };
    setChargeRows(next);
  };
  const onDiscountCellValueChanged = (params: any) => {
    if (!canEditCurrentPO) return;
    const key = String(params.data?.key || '');
    const val = Number(params.newValue) || 0;
    if (key === 'pct') {
      headerDiscountModeRef.current = 'pct';
      setHeaderDiscPct(val);
      return;
    }
    if (key === 'amt') {
      headerDiscountModeRef.current = 'amt';
      setHeaderDiscAmt(val);
    }
  };
  const onTaxCellValueChanged = (params: any) => {
    if (!canEditCurrentPO) return;
    const key = String(params.data?.key || '');
    const val = Number(params.newValue) || 0;
    if (key === 'pct') {
      taxModeRef.current = 'pct';
      setTaxPct(val);
      return;
    }
    if (key === 'amt') {
      taxModeRef.current = 'amt';
      setTaxAmt(val);
    }
  };

  const handleCopyPRToPO = async (prId: number) => {
    if (!canEditCurrentPO) return;
    try {
      const response = await apiService.getPR(prId);
      const prData = (response as any)?.data || response || {};
      const prLines = (prData.lines || prData.Lines || []) as any[];

      const nextReqType = String(prData.requisitiontype || prData.RequisitionType || 'Inventory') as 'Inventory' | 'Service' | 'FixedAsset' | 'Subcontracting';
      const nextAllocType = String(prData.allocationtype || prData.AllocationType || 'Project') as 'Project' | 'CostCenter';
      const nextAllocCode = String(prData.allocationcode || prData.AllocationCode || '');
      const nextFacility = String(prData.facilitytype || prData.FacilityType || 'NA');
      const nextCycle = String(prData.cycle || prData.Cycle || 'Project');
      const nextDueDate = String(prData.requireddate || prData.RequiredDate || '').slice(0, 10);

      const firstVendorLine = prLines.find((ln: any) => String(ln.vendorcode || ln.VendorCode || '').trim()) || prLines[0] || {};
      const nextVendorCode = String(firstVendorLine.vendorcode || firstVendorLine.VendorCode || '');
      const nextVendorName = String(firstVendorLine.vendorname || firstVendorLine.VendorName || '');
      const nextCurrency = String(prData.currency || prData.Currency || 'SAR').toUpperCase();

      const mappedLines: POLine[] = prLines
        .map((ln: any) => ({
        itemtype: nextReqType,
        itemcode: ln.itemcode || ln.ItemCode || '',
        itemdescription: ln.itemdescription || ln.ItemDescription || '',
        quantity: Number(ln.openqty ?? ln.OpenQty ?? ln.quantity ?? ln.Quantity ?? 1),
        openqty: Number(ln.openqty ?? ln.OpenQty ?? ln.quantity ?? ln.Quantity ?? 0),
        closedqty: Number(ln.closedqty ?? ln.ClosedQty ?? 0),
        uomcode: ln.uomcode || ln.UoMCode || '',
        unitprice: Number(ln.unitprice || ln.UnitPrice || 0),
        accountcode: ln.accountcode || ln.AccountCode || '',
        warehouse: ln.warehouse || ln.Warehouse || '',
        projectcode: ln.projectcode || ln.ProjectCode || (nextAllocType === 'Project' ? nextAllocCode : ''),
        dim1: ln.dim1 || ln.Dim1 || '',
        dim2: ln.dim2 || ln.Dim2 || (nextAllocType === 'CostCenter' ? nextAllocCode : ''),
        dim3: ln.dim3 || ln.Dim3 || '',
        dim4: ln.dim4 || ln.Dim4 || '',
        dim5: ln.dim5 || ln.Dim5 || '',
        deliverydate: String(ln.deliverydate || ln.DeliveryDate || '').slice(0, 10),
        lineremarks: ln.u_item_details || ln.lineremarks || ln.LineRemarks || '',
        discpct: Number(ln.discpct || ln.DiscPrcnt || 0),
        discamt: Number(ln.discamt || ln.DiscSum || 0),
        sourceprid: Number(prId),
        sourceprlinenum: Number(ln.linenum || ln.LineNum || 0),
      }))
        .filter((ln) => Number(ln.openqty || 0) > 0);

      if (!mappedLines.length) {
        dispatch(showSnackbar({ message: `PR ${prData.prnumber || prData.PRNumber || prId} has no open quantity left for PO creation`, severity: 'error' }));
        return;
      }

      setRequisitionType(nextReqType);
      setAllocationType(nextAllocType);
      setAllocationCode(nextAllocCode);
      setFacilityType(nextFacility);
      setCycle(nextCycle);
      setDueDate(nextDueDate);
      setRemarks(String(prData.remarks || prData.Remarks || ''));
      setVendorCode(nextVendorCode);
      setVendorName(nextVendorName);
      setCurrency(currencyOptions.includes(nextCurrency) ? nextCurrency : 'SAR');
      setSelectedApprovalFlow(String(prData.approvalflowname || prData.ApprovalFlowName || ''));
      setCopyFromPRId(Number(prId));
      setLines(mappedLines.length ? mappedLines : [createEmptyLine()]);
      setCopyDialogOpen(false);

      dispatch(showSnackbar({ message: `PR ${prData.prnumber || prData.PRNumber || prId} copied to PO`, severity: 'success' }));
    } catch (error: any) {
      const msg = error?.response?.data?.message || 'Failed to copy posted PR';
      dispatch(showSnackbar({ message: msg, severity: 'error' }));
    }
  };

  const loadPOById = async (poId: number, options?: { silent?: boolean }) => {
    try {
      const response = await apiService.getPO(poId);
      const poData = (response as any)?.data || response || {};
      const poLines = (poData.lines || poData.Lines || []) as any[];
      const additionalChargesRaw = (poData.additionalcharges ?? poData.AdditionalCharges) as any;
      const additionalCharges = Array.isArray(additionalChargesRaw)
        ? additionalChargesRaw
        : (() => {
            if (typeof additionalChargesRaw !== 'string' || !additionalChargesRaw.trim()) return [];
            try {
              const parsed = JSON.parse(additionalChargesRaw);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          })();

      const nextReqType = String(poData.requisitiontype || poData.RequisitionType || 'Inventory') as 'Inventory' | 'Service' | 'FixedAsset' | 'Subcontracting';
      const nextAllocType = String(poData.allocationtype || poData.AllocationType || 'Project') as 'Project' | 'CostCenter';
      const nextAllocCode = String(poData.allocationcode || poData.AllocationCode || '');
      const nextFacility = String(poData.facilitytype || poData.FacilityType || 'NA');
      const nextCycle = String(poData.cycle || poData.Cycle || 'Project');
      const nextStatus = String(poData.status || poData.Status || 'Draft');
      const nextRemarks = String(poData.remarks || poData.Remarks || '');
      const nextPostingDate = String(poData.postingdate || poData.PostingDate || poData.podate || poData.PODate || '').slice(0, 10);
      const nextDocumentDate = String(poData.documentdate || poData.DocumentDate || '').slice(0, 10);
      const nextDueDate = String(poData.duedate || poData.DueDate || poData.requireddate || poData.RequiredDate || '').slice(0, 10);
      const nextVendorCode = String(poData.vendorcode || poData.VendorCode || '');
      const nextVendorName = String(poData.vendorname || poData.VendorName || '');
      const nextPaymentTermCode = String(poData.paymenttermcode || poData.PaymentTermCode || '');
      const nextPaymentTermName = String(poData.paymenttermname || poData.PaymentTermName || '');
      const nextDeliveryType = String(poData.deliverytype || poData.DeliveryType || '');
      const nextIncoterms = String(poData.incoterms || poData.Incoterms || '');
      const nextModeOfShipment = String(poData.modeofshipment || poData.ModeOfShipment || '');
      const nextRequiredDocuments = String(poData.requireddocuments || poData.RequiredDocuments || '');
      const nextConsignee = String(poData.consignee || poData.Consignee || '');
      const nextShipmentRequirements = String(poData.shipmentrequirements || poData.ShipmentRequirements || '');
      const nextCurrency = String(poData.currency || poData.Currency || 'SAR').toUpperCase();
      const nextChargeRows = baseChargeRows.map((row) => ({ ...row }));
      additionalCharges.forEach((charge: any) => {
        const chargeType = String(charge?.type || charge?.Type || '').trim();
        const chargeAmount = Number(charge?.amount ?? charge?.Amount ?? 0) || 0;
        if (!chargeType) return;
        const existing = nextChargeRows.find((row) => row.type === chargeType);
        if (existing) {
          existing.amount = chargeAmount;
          return;
        }
        nextChargeRows.push({ type: chargeType as ChargeRow['type'], amount: chargeAmount });
      });
      const fallbackChargeMap: Record<ChargeRow['type'], number> = {
        'OTHERS ESTIMATED CHARGES (Finance, Insurance, Installation)': Number(poData.othercharges || poData.OtherCharges || 0) || 0,
        'FREIGHT CHARGES': Number(poData.freight || poData.Freight || 0) || 0,
        'CUSTOMS CHARGES': Number(poData.customduty || poData.CustomDuty || 0) || 0,
        'DELIVERY ORDER': 0,
        'LOCAL CLEARANCE CHARGES': 0,
      };
      nextChargeRows.forEach((row) => {
        if ((Number(row.amount) || 0) === 0 && fallbackChargeMap[row.type] !== undefined) {
          row.amount = Number(fallbackChargeMap[row.type]) || 0;
        }
      });

      const mappedLines: POLine[] = poLines.map((ln: any) => ({
        itemtype: nextReqType,
        itemcode: ln.itemcode || ln.ItemCode || '',
        itemdescription: ln.itemdescription || ln.ItemDescription || '',
        quantity: Number(ln.quantity || ln.Quantity || 1),
        openqty: Number(ln.openqty || ln.OpenQty || 0),
        closedqty: Number(ln.closedqty || ln.ClosedQty || 0),
        uomcode: ln.uomcode || ln.UoMCode || '',
        unitprice: Number(ln.unitprice || ln.UnitPrice || 0),
        accountcode: ln.accountcode || ln.AccountCode || '',
        warehouse: ln.warehouse || ln.Warehouse || '',
        projectcode: ln.projectcode || ln.ProjectCode || (nextAllocType === 'Project' ? nextAllocCode : ''),
        dim1: ln.dim1 || ln.Dim1 || '',
        dim2: ln.dim2 || ln.Dim2 || (nextAllocType === 'CostCenter' ? nextAllocCode : ''),
        dim3: ln.dim3 || ln.Dim3 || '',
        dim4: ln.dim4 || ln.Dim4 || '',
        dim5: ln.dim5 || ln.Dim5 || '',
        deliverydate: String(ln.deliverydate || ln.DeliveryDate || '').slice(0, 10),
        lineremarks: ln.u_item_details || ln.lineremarks || ln.LineRemarks || '',
        discpct: Number(ln.discpct || ln.DiscPrcnt || 0),
        discamt: Number(ln.discamt || ln.DiscSum || 0),
        sourceprid: Number(ln.sourceprid || ln.SourcePRID || 0) || undefined,
        sourceprlinenum: Number(ln.sourceprlinenum || ln.SourcePRLineNum || 0) || undefined,
      }));

      setCreatedPoId(Number(poData.poid || poData.POID || poId));
      setPoNumber(String(poData.ponumber || poData.PONumber || ''));
      setPoStatus(nextStatus);
      try {
        const historyResponse = await apiService.getPOApprovalHistory(Number(poId));
        const historyRows = ((historyResponse as any)?.data || historyResponse || []) as any[];
        const latest = [...historyRows]
          .sort((a: any, b: any) => {
            const left = new Date(a?.actiondate || a?.ActionDate || 0).getTime();
            const right = new Date(b?.actiondate || b?.ActionDate || 0).getTime();
            return right - left;
          })[0];
        setLastWorkflowAction(String(latest?.action || latest?.Action || ''));
        setLastWorkflowComment(String(latest?.comments || latest?.Comments || ''));
        setLastWorkflowActor(String(latest?.approvername || latest?.ApproverName || ''));
      } catch {
        setLastWorkflowAction('');
        setLastWorkflowComment('');
        setLastWorkflowActor('');
      }
      setRequisitionType(nextReqType);
      setAllocationType(nextAllocType);
      setAllocationCode(nextAllocCode);
      setFacilityType(nextFacility);
      setCycle(nextCycle);
      if (nextPostingDate) setPostingDate(nextPostingDate);
      if (nextDocumentDate) setDocumentDate(nextDocumentDate);
      if (nextDueDate) setDueDate(nextDueDate);
      setRemarks(nextRemarks);
      setVendorCode(nextVendorCode);
      setVendorName(nextVendorName);
      setPaymentTermCode(nextPaymentTermCode);
      setPaymentTermName(nextPaymentTermName);
      setDeliveryType(nextDeliveryType);
      setIncoterms(nextIncoterms);
      setModeOfShipment(nextModeOfShipment);
      setRequiredDocuments(nextRequiredDocuments);
      setConsignee(nextConsignee);
      setShipmentRequirements(nextShipmentRequirements);
      setCurrency(currencyOptions.includes(nextCurrency) ? nextCurrency : 'SAR');
      setSellingPrice(Number(poData.sellingprice || poData.SellingPrice || 0) || 0);
      setHeaderDiscPct(Number(poData.headerdiscpct || poData.HeaderDiscPct || 0) || 0);
      setHeaderDiscAmt(Number(poData.headerdiscamt || poData.HeaderDiscAmt || 0) || 0);
      setTaxPct(Number(poData.taxpct || poData.TaxPct || 0) || 0);
      setTaxAmt(Number(poData.taxamt || poData.TaxAmt || 0) || 0);
      setChargeRows(nextChargeRows);
      if (poData.chargeexpensecodes !== undefined) setChargeCodes(readChargeExpenseCodes(poData.chargeexpensecodes));
      setSelectedApprovalFlow(String(poData.approvalflowname || poData.ApprovalFlowName || ''));
      setCopyFromPRId(Number(mappedLines.find((ln) => Number(ln.sourceprid || 0) > 0)?.sourceprid || 0) || null);
      setLines(mappedLines.length ? mappedLines : [createEmptyLine()]);
      setPoSearchOpen(false);
      setPoSearchQuery('');
      if (!options?.silent) {
        dispatch(showSnackbar({ message: `PO ${poData.ponumber || poData.PONumber || poId} loaded`, severity: 'success' }));
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || 'Failed to load PO';
      dispatch(showSnackbar({ message: msg, severity: 'error' }));
    }
  };

  const handleGridContextMenu = (params: any) => {
    if (!canEditCurrentPO) return;
    params.event?.preventDefault?.();
    const rowIdx = lines.indexOf(params.data as POLine);
    setContextMenu(
      rowIdx >= 0
        ? { mouseX: params.event.clientY - 4, mouseY: params.event.clientX - 2, rowIndex: rowIdx }
        : null
    );
  };

  const handleCopyRow = (rowIndex: number) => {
    if (rowIndex < 0 || rowIndex >= lines.length) return;
    setClipboardRow({ ...lines[rowIndex] });
    setClipboardMode('copy');
    dispatch(showSnackbar({ message: 'Row copied', severity: 'success' }));
  };

  const handleCutRow = (rowIndex: number) => {
    if (!canEditCurrentPO) return;
    if (rowIndex < 0 || rowIndex >= lines.length) return;
    setClipboardRow({ ...lines[rowIndex] });
    setClipboardMode('cut');
    setLines((prev) => {
      const n = prev.filter((_, idx) => idx !== rowIndex);
      return n.length ? n : [createEmptyLine()];
    });
    dispatch(showSnackbar({ message: 'Row cut', severity: 'success' }));
  };

  const handlePasteRow = (rowIndex: number) => {
    if (!canEditCurrentPO) return;
    if (!clipboardRow) {
      dispatch(showSnackbar({ message: 'No row to paste', severity: 'warning' }));
      return;
    }
    setLines((prev) => {
      const n = [...prev];
      const at = rowIndex < 0 ? 0 : rowIndex + 1;
      n.splice(at, 0, { ...clipboardRow });
      return n;
    });
    if (clipboardMode === 'cut') {
      setClipboardRow(null);
      setClipboardMode(null);
    }
    dispatch(showSnackbar({ message: 'Row pasted', severity: 'success' }));
  };

  const filledPoLineCount = lines.filter((l) => String(l.itemcode || '').trim() || String(l.itemdescription || '').trim()).length;
  const poSubmitProblems = canEditCurrentPO ? collectPOValidationErrors(false) : [];
  const totalsButtonSx = {
    p: 0,
    minHeight: 0,
    minWidth: 0,
    color: EDITOR_COLORS.blueText,
    fontWeight: 600,
    fontSize: 13,
    justifyContent: 'flex-start',
    textAlign: 'left',
    '& .MuiButton-endIcon': { ml: 0.5 },
  } as const;

  return (
    <Paper
      component="section"
      aria-labelledby="create-po-heading"
      elevation={0}
      sx={{ p: { xs: 1, sm: 1.5, md: 2 }, bgcolor: '#f7f7f7', border: 0, borderRadius: 0, height: 'calc(100vh - 64px)', overflow: 'auto' }}
    >
      {/* Page header: which PO this is, finding one, copying a posted PR, closing */}
      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, bgcolor: '#ffffff' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
          <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
            <Typography
              component="h1"
              id="create-po-heading"
              sx={{ m: 0, fontSize: { xs: 18, sm: 22 }, fontWeight: 600, lineHeight: 1.25, color: EDITOR_COLORS.text }}
            >
              {isReworkedPO ? 'Rework purchase order' : createdPoId ? 'Purchase order' : 'New purchase order'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.75, fontSize: 12, color: EDITOR_COLORS.muted }}>
              <StatusPill status={poStatus || 'Draft'} />
              {poNumber ? <span>{poNumber}</span> : null}
              {!createdPoId ? <span>Not saved yet</span> : null}
              {vendorName ? <span>{vendorName}</span> : null}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <Button variant="outlined" startIcon={<SearchIcon />} onClick={() => setPoSearchOpen(true)} sx={secondaryButtonSx}>Search PO</Button>
            <Button variant="outlined" onClick={() => setCopyDialogOpen(true)} disabled={!canEditCurrentPO} sx={secondaryButtonSx}>Copy Posted PR</Button>
            {createdPoId && (
              <Button variant="outlined" onClick={() => navigate(`/po/${createdPoId}`)} sx={secondaryButtonSx}>
                View PO
              </Button>
            )}
            <IconButton onClick={handleClose} aria-label="Close" title="Close"><CloseIcon /></IconButton>
          </Box>
        </Box>
      </Paper>

      <EditorLayout
        main={
          <>
            {createdPoId && isReworkedPO && (
              <Box sx={{ p: 2, bgcolor: '#fff8e1', border: '1px solid #f9a825', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Rework Requested
                </Typography>
                <Typography variant="body2">
                  This PO was sent back for rework. Update the same PO and resubmit. The PO number and approval history will be retained.
                </Typography>
                {(lastWorkflowActor || lastWorkflowComment) && (
                  <Typography variant="body2" sx={{ mt: 0.75 }}>
                    {lastWorkflowActor ? `By: ${lastWorkflowActor}` : ''}
                    {lastWorkflowActor && lastWorkflowComment ? ' | ' : ''}
                    {lastWorkflowComment ? `Comment: ${lastWorkflowComment}` : ''}
                  </Typography>
                )}
              </Box>
            )}

            {canEditCurrentPO && requisitionType === 'Subcontracting' && (
              <Box sx={{ px: 1.5, py: 1, bgcolor: '#e8f5e9', border: '1px solid #43a047', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Subcontracting PO will post directly to SAP and bypass approval flow.
                </Typography>
              </Box>
            )}

            {canEditCurrentPO && canUseDirectPostToSAPTest && (
              <Box sx={{ px: 1.5, py: 1, bgcolor: '#fff8e1', border: '1px solid #f9a825', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={directPostToSAPTest}
                      onChange={(e) => setDirectPostToSAPTest(e.target.checked)}
                    />
                  }
                  label="Test mode: post this subcontract/service PO directly to SAP and bypass approval flow"
                />
              </Box>
            )}

            <EditorSection id="po-sec-vendor" number={1} title="Vendor">
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                <TextField
                  size="small"
                  label="Vendor"
                  value={vendorName || vendorCode}
                  onClick={() => { if (canEditCurrentPO) setVendorPickerOpen(true); }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon fontSize="small" sx={{ color: EDITOR_COLORS.muted, cursor: 'pointer' }} />
                      </InputAdornment>
                    )
                  }}
                  helperText={vendorName && vendorCode ? vendorCode : 'Click to choose the vendor'}
                  fullWidth
                  sx={{ cursor: canEditCurrentPO ? 'pointer' : 'default' }}
                />

                <FormControl size="small" fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={currency}
                    label="Currency"
                    onChange={(e) => setCurrency(String(e.target.value || 'SAR'))}
                    disabled={!canEditCurrentPO}
                  >
                    {currencyOptions.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>Payment Terms</InputLabel>
                  <Select
                    value={paymentTermCode}
                    label="Payment Terms"
                    onChange={(e) => setPaymentTermCode(String(e.target.value || ''))}
                    disabled={!canEditCurrentPO}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {paymentTermOptions.map((p: any) => {
                      const code = String(p.GroupNumber || p.groupnumber || '');
                      const name = String(p.PaymentTermsGroupName || p.paymenttermsgroupname || p.Name || '');
                      return <MenuItem key={code} value={code}>{code}{name ? ` - ${name}` : ''}</MenuItem>;
                    })}
                  </Select>
                </FormControl>
              </Box>
            </EditorSection>

            <EditorSection id="po-sec-details" number={2} title="Order details">
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={requisitionType}
                    label="Type"
                    onChange={(e) => setRequisitionType(e.target.value as any)}
                    disabled={!canEditCurrentPO}
                  >
                    <MenuItem value="Inventory">Material</MenuItem>
                    <MenuItem value="Service">Service</MenuItem>
                    <MenuItem value="FixedAsset">Asset</MenuItem>
                    <MenuItem value="Subcontracting">Subcontracting</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>Allocation</InputLabel>
                  <Select
                    value={allocationType}
                    label="Allocation"
                    onChange={(e) => {
                      setAllocationType(e.target.value as any);
                      setAllocationCode('');
                      setAllocationName('');
                    }}
                    disabled={!canEditCurrentPO}
                  >
                    <MenuItem value="Project">Project</MenuItem>
                    <MenuItem value="CostCenter">Cost Center</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label={allocationType === 'Project' ? 'Project' : 'Cost Center'}
                  value={allocationName || allocationCode}
                  onClick={() => { if (canEditCurrentPO) setProjectPickerOpen(true); }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon fontSize="small" sx={{ color: EDITOR_COLORS.muted, cursor: 'pointer' }} />
                      </InputAdornment>
                    )
                  }}
                  fullWidth
                  sx={{ cursor: canEditCurrentPO ? 'pointer' : 'default' }}
                  helperText={allocationName || ''}
                />

                <FormControl size="small" fullWidth>
                  <InputLabel>Approval Flow</InputLabel>
                  <Select
                    value={selectedApprovalFlow}
                    label="Approval Flow"
                    onChange={(e) => setSelectedApprovalFlow(String(e.target.value || ''))}
                    disabled={!canEditCurrentPO || loadingApprovalFlows || availableApprovalFlows.length === 0}
                  >
                    {availableApprovalFlows.map((f: any) => {
                      const name = String(f.flowname || '');
                      return <MenuItem key={name} value={name}>{name}</MenuItem>;
                    })}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
                <TextField
                  size="small"
                  type="date"
                  label="Posting Date"
                  value={postingDate}
                  onChange={(e) => setPostingDate(e.target.value)}
                  onBlur={(e) => setPostingDate(fixTwoDigitYear(e.target.value))}
                  disabled={!canEditCurrentPO}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />

                <TextField
                  size="small"
                  type="date"
                  label="Due Date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  onBlur={(e) => setDueDate(fixTwoDigitYear(e.target.value))}
                  disabled={!canEditCurrentPO}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                />

                <TextField
                  size="small"
                  type="date"
                  label="Document Date"
                  value={documentDate}
                  onChange={(e) => setDocumentDate(e.target.value)}
                  onBlur={(e) => setDocumentDate(fixTwoDigitYear(e.target.value))}
                  disabled={!canEditCurrentPO}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />

                <FormControl size="small" fullWidth>
                  <InputLabel>Facility</InputLabel>
                  <Select
                    value={facilityType}
                    label="Facility"
                    onChange={(e) => setFacilityType(e.target.value)}
                    disabled={!canEditCurrentPO}
                  >
                    <MenuItem value="NA">NA</MenuItem>
                    <MenuItem value="Commercial">Commercial</MenuItem>
                    <MenuItem value="Residential">Residential</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>Cycle</InputLabel>
                  <Select
                    value={cycle}
                    label="Cycle"
                    onChange={(e) => setCycle(e.target.value)}
                    disabled={!canEditCurrentPO}
                  >
                    <MenuItem value="Project">Project</MenuItem>
                    <MenuItem value="Department">Department</MenuItem>
                    <MenuItem value="HR">HR</MenuItem>
                    <MenuItem value="Admin">Admin</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  type="number"
                  label="Selling Price"
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(Number(e.target.value) || 0)}
                  disabled={!canEditCurrentPO}
                  fullWidth
                />
              </Box>
            </EditorSection>

            <EditorSection
              id="po-sec-items"
              number={3}
              title="Items"
              note={`· ${filledPoLineCount} ${filledPoLineCount === 1 ? 'line' : 'lines'}`}
              disableBodyPadding
              actions={
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addLine} disabled={!canEditCurrentPO} sx={addButtonSx}>
                    Add Row
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<SearchIcon />} onClick={() => setItemPickerOpen(true)} disabled={!canEditCurrentPO} sx={secondaryButtonSx}>
                    Item Search
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={removeSelectedLines}
                    disabled={!canEditCurrentPO}
                    sx={{ color: EDITOR_COLORS.red, borderColor: '#d9a3a3', bgcolor: '#ffffff', '&:hover': { borderColor: EDITOR_COLORS.red, bgcolor: '#fff5f5' } }}
                  >
                    Delete Selected
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={(e) => {
                      setGridMenuAnchor(e.currentTarget);
                      setColumnsOpen(false);
                      setDensityOpen(false);
                    }}
                    sx={secondaryButtonSx}
                  >
                    Grid
                  </Button>
                </Box>
              }
            >
              <Box
                className="ag-theme-alpine"
                sx={{
                  height: 460,
                  width: '100%',
                  borderTop: `1px solid ${EDITOR_COLORS.divider}`,
                  '& .ag-cell-inline-editing': {
                    padding: 0,
                    display: 'flex',
                    alignItems: 'stretch'
                  },
                  '& .ag-cell-inline-editing .ag-cell-edit-wrapper': {
                    width: '100%',
                    height: '100%'
                  },
                  '& .ag-cell-inline-editing .ag-input-field-input, & .ag-cell-inline-editing input': {
                    width: '100% !important',
                    height: '100% !important',
                    minHeight: '24px',
                    padding: '4px 8px !important',
                    fontSize: gridFontSize,
                    lineHeight: 1.3,
                    border: '1px solid #94a3b8',
                    borderRadius: 0,
                    boxSizing: 'border-box'
                  }
                }}
              >
                <AgGridReact
                  ref={lineGridRef}
                  rowData={lines}
                  columnDefs={lineColumnDefs as any}
                  defaultColDef={{ resizable: true, sortable: true, filter: true }}
                  rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: true } as any}
                  animateRows
                  suppressRowClickSelection={false}
                  onCellValueChanged={onLineCellValueChanged}
                  onCellDoubleClicked={onLineCellDoubleClicked}
                  onCellContextMenu={handleGridContextMenu}
                  rowHeight={rowHeight}
                  headerHeight={headerHeight}
                  suppressNoRowsOverlay
                />
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 2, px: 2, py: 1.5, borderTop: `1px solid ${EDITOR_COLORS.divider}`, bgcolor: '#f9fafb' }}>
                <Typography sx={{ flex: '1 1 240px', fontSize: 12, color: EDITOR_COLORS.muted }}>
                  Double-click an item, warehouse, project or dimension cell to choose from the list. Amounts are in {currency}.
                </Typography>
                <Box component="dl" sx={{ flex: '0 1 380px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', columnGap: 3, rowGap: 0.5, m: 0, fontSize: 13 }}>
                  <Box component="dt" sx={{ color: EDITOR_COLORS.muted }}>Line gross</Box>
                  <Box component="dd" sx={{ m: 0, textAlign: 'right' }}>{lineGrossTotal.toFixed(2)}</Box>
                  {lineDiscountTotal > 0 && (
                    <>
                      <Box component="dt" sx={{ color: EDITOR_COLORS.muted }}>Line discounts</Box>
                      <Box component="dd" sx={{ m: 0, textAlign: 'right' }}>-{lineDiscountTotal.toFixed(2)}</Box>
                    </>
                  )}
                  <Box component="dt">
                    <Button variant="text" size="small" onClick={() => { if (canEditCurrentPO) setDiscountDialogOpen(true); }} disabled={!canEditCurrentPO} endIcon={<ArrowForwardIosIcon sx={{ fontSize: '11px !important' }} />} sx={totalsButtonSx}>
                      Discount
                    </Button>
                  </Box>
                  <Box component="dd" sx={{ m: 0, textAlign: 'right' }}>{headerDiscAmt > 0 ? `-${headerDiscAmt.toFixed(2)}` : '0.00'}</Box>
                  <Box component="dt">
                    <Button variant="text" size="small" onClick={() => { if (canEditCurrentPO) setTaxDialogOpen(true); }} disabled={!canEditCurrentPO} endIcon={<ArrowForwardIosIcon sx={{ fontSize: '11px !important' }} />} sx={totalsButtonSx}>
                      Tax (estimate)
                    </Button>
                  </Box>
                  <Box component="dd" sx={{ m: 0, textAlign: 'right' }}>{taxAmt.toFixed(2)}</Box>
                  <Box component="dt">
                    <Button variant="text" size="small" onClick={() => { if (canEditCurrentPO) setChargesDialogOpen(true); }} disabled={!canEditCurrentPO} endIcon={<ArrowForwardIosIcon sx={{ fontSize: '11px !important' }} />} sx={totalsButtonSx}>
                      Additional Charges
                    </Button>
                  </Box>
                  <Box component="dd" sx={{ m: 0, textAlign: 'right' }}>{chargeTotal.toFixed(2)}</Box>
                  <Box aria-hidden="true" sx={{ gridColumn: '1 / -1', mt: 0.5, borderTop: `1px solid ${EDITOR_COLORS.border}` }} />
                  <Box component="dt" sx={{ pt: 0.5, fontSize: 15, fontWeight: 700 }}>Grand Total</Box>
                  <Box component="dd" sx={{ m: 0, pt: 0.5, textAlign: 'right', fontSize: 17, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {grandTotal.toFixed(2)} {currency}
                  </Box>
                  {showSapAmount && (
                    <>
                      <Box component="dt" sx={{ color: EDITOR_COLORS.muted, fontSize: 12 }}>SAP PO before VAT</Box>
                      <Box component="dd" sx={{ m: 0, textAlign: 'right', color: EDITOR_COLORS.muted, fontSize: 12 }}>{sapAmountBeforeVat.toFixed(2)}</Box>
                      <Box sx={{ gridColumn: '1 / -1', color: EDITOR_COLORS.muted, fontSize: 11.5 }}>
                        SAP gets the lines, the discount and the charges marked SAP freight, and adds VAT itself. The tax and the other charges are PRPO estimates.
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </EditorSection>

            <EditorSection id="po-sec-footer" number={4} title="Remarks and shipping" note="· remarks go to SAP; the rest prints on the PO">
              <TextField
                size="small"
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                disabled={!canEditCurrentPO}
                multiline
                minRows={2}
                fullWidth
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                <TextField
                  size="small"
                  label="Delivery"
                  value={deliveryType}
                  onChange={(e) => setDeliveryType(e.target.value)}
                  disabled={!canEditCurrentPO}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Incoterms"
                  value={incoterms}
                  onChange={(e) => setIncoterms(e.target.value)}
                  disabled={!canEditCurrentPO}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Mode of Shipment"
                  value={modeOfShipment}
                  onChange={(e) => setModeOfShipment(e.target.value)}
                  disabled={!canEditCurrentPO}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Consignee"
                  value={consignee}
                  onChange={(e) => setConsignee(e.target.value)}
                  disabled={!canEditCurrentPO}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Required Documents"
                  value={requiredDocuments}
                  onChange={(e) => setRequiredDocuments(e.target.value)}
                  disabled={!canEditCurrentPO}
                  multiline
                  minRows={2}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Shipment Requirements"
                  value={shipmentRequirements}
                  onChange={(e) => setShipmentRequirements(e.target.value)}
                  disabled={!canEditCurrentPO}
                  multiline
                  minRows={2}
                  fullWidth
                />
              </Box>
            </EditorSection>

            <EditorSection
              id="po-sec-files"
              number={5}
              title="Attachments"
              note={attachments.length ? `· ${attachments.length}` : undefined}
              actions={
                <Stack direction="row" spacing={1}>
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => { void uploadAttachments(e.target.files); }}
                  />
                  <Button variant="outlined" size="small" disabled={!createdPoId || uploadingAttachments || !canEditCurrentPO} onClick={() => attachmentInputRef.current?.click()} sx={addButtonSx}>
                    Upload
                  </Button>
                  <Button variant="outlined" size="small" disabled={!createdPoId} onClick={() => { void refreshAttachments(); }} sx={secondaryButtonSx}>
                    Refresh
                  </Button>
                </Stack>
              }
            >
              {!createdPoId && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Save draft or submit PO first, then upload attachments.
                </Typography>
              )}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f7f8' }}>
                      <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Uploaded By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Uploaded Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attachments.map((a: any, i: number) => {
                      const aid = Number(a.attachmentid ?? a.AttachmentID ?? i);
                      return (
                        <TableRow key={aid}>
                          <TableCell>{a.filename || a.FileName || ''}</TableCell>
                          <TableCell>{Number(a.filesize || a.FileSize || 0)}</TableCell>
                          <TableCell>{a.mimetype || a.filetype || a.MimeType || a.FileType || ''}</TableCell>
                          <TableCell>{a.uploadedbyname || a.UploadedByName || ''}</TableCell>
                          <TableCell>{a.uploadeddate || a.UploadedDate || ''}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              {createdPoId && <Button size="small" href={apiService.getPOAttachmentViewUrl(createdPoId, aid)} target="_blank">View</Button>}
                              {createdPoId && <Button size="small" href={apiService.getPOAttachmentDownloadUrl(createdPoId, aid)}>Download</Button>}
                              {createdPoId && <Button size="small" color="error" disabled={!canEditCurrentPO} onClick={() => { void removeAttachment(aid); }}>Delete</Button>}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {attachments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>No attachments</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </EditorSection>
          </>
        }
        side={
          <>
            <SideCard id="po-side-summary" title="Summary">
              <Box sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2, color: EDITOR_COLORS.text }}>
                {grandTotal.toFixed(2)}{' '}
                <Box component="span" sx={{ fontSize: 14, fontWeight: 600, color: EDITOR_COLORS.muted }}>{currency}</Box>
              </Box>
              <Box sx={{ mb: 1, fontSize: 12, color: EDITOR_COLORS.muted }}>Grand total</Box>
              <SideFact label="Lines">{filledPoLineCount}</SideFact>
              <SideFact label="Line gross">{lineGrossTotal.toFixed(2)}</SideFact>
              {showSapAmount && <SideFact label="SAP PO before VAT">{sapAmountBeforeVat.toFixed(2)}</SideFact>}
              <SideFact label="Vendor">{vendorName || vendorCode || '—'}</SideFact>
              <SideFact label={allocationType === 'Project' ? 'Project' : 'Cost center'}>{allocationCode || '—'}</SideFact>
              <Divider sx={{ my: 1.5 }} />
              {canEditCurrentPO ? (
                <SubmitChecklist problems={poSubmitProblems} />
              ) : (
                <Box sx={{ fontSize: 13, color: EDITOR_COLORS.muted }}>Only draft or rejected POs can be changed here.</Box>
              )}
              <Button
                fullWidth
                variant="contained"
                onClick={() => handleSubmit(false)}
                disabled={actionButtonsDisabled}
                sx={{ mt: 2, minHeight: 40, fontSize: 14, fontWeight: 700 }}
              >
                Submit for Approval
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleSubmit(true)}
                disabled={actionButtonsDisabled}
                sx={{ ...secondaryButtonSx, mt: 1, minHeight: 36 }}
              >
                Save as Draft
              </Button>
            </SideCard>

            <SideCard id="po-side-route" title="Approval">
              {requisitionType === 'Subcontracting' ? (
                <Box sx={{ fontSize: 13, color: EDITOR_COLORS.muted }}>Subcontracting POs post straight to SAP, without an approval flow.</Box>
              ) : loadingApprovalFlows ? (
                <Box sx={{ fontSize: 13, color: EDITOR_COLORS.muted }}>Finding the approval flows…</Box>
              ) : selectedApprovalFlow ? (
                <>
                  <Box sx={{ fontSize: 13, fontWeight: 600, color: EDITOR_COLORS.green }}>{selectedApprovalFlow}</Box>
                  <Box sx={{ mt: 0.5, fontSize: 12, color: EDITOR_COLORS.muted }}>
                    {availableApprovalFlows.length > 1
                      ? `${availableApprovalFlows.length} flows match this PO; you can change it in Order details.`
                      : 'The flow that matches this PO.'}
                  </Box>
                </>
              ) : availableApprovalFlows.length > 0 ? (
                <Box sx={{ fontSize: 13, fontWeight: 600, color: EDITOR_COLORS.amber }}>Choose the approval flow in Order details.</Box>
              ) : (
                <Box sx={{ fontSize: 13, color: EDITOR_COLORS.muted }}>
                  {allocationCode ? 'No approval flow matches this PO yet. Ask your administrator to set one up.' : 'Choose the project or cost center to find the approval flow.'}
                </Box>
              )}
            </SideCard>
          </>
        }
      />

      {/* Project/Cost Center Picker Dialog */}
      <Dialog open={projectPickerOpen} onClose={() => { setProjectPickerOpen(false); setProjectSearchQuery(''); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Select {allocationType === 'Project' ? 'Project' : 'Cost Center'}
            <IconButton onClick={() => { setProjectPickerOpen(false); setProjectSearchQuery(''); }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            placeholder="Search..."
            value={projectSearchQuery}
            onChange={(e) => setProjectSearchQuery(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#999' }} />
            }}
          />
          <Box className="ag-theme-alpine" sx={{ height: 360, width: '100%' }}>
            <AgGridReact
              rowData={filteredProjectOptions.slice(0, 200)}
              columnDefs={[
                { field: allocationType === 'Project' ? 'Code' : 'FactorCode', headerName: 'Code', flex: 1 },
                { field: allocationType === 'Project' ? 'Name' : 'FactorDescription', headerName: 'Name', flex: 2 },
              ]}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              onRowClicked={(p: any) => handleAllocationSelect(p.data)}
              onRowDoubleClicked={(p: any) => handleAllocationSelect(p.data)}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Vendor Picker Dialog */}
      <Dialog open={vendorPickerOpen} onClose={() => { setVendorPickerOpen(false); setVendorSearchQuery(''); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Select Vendor
            <IconButton onClick={() => { setVendorPickerOpen(false); setVendorSearchQuery(''); }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            placeholder="Search vendors..."
            value={vendorSearchQuery}
            onChange={(e) => setVendorSearchQuery(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#999' }} />
            }}
          />
          <Box className="ag-theme-alpine" sx={{ height: 360, width: '100%' }}>
            <AgGridReact
              rowData={filteredVendorOptions.slice(0, 200)}
              columnDefs={[
                { field: 'CardCode', headerName: 'Code', flex: 1 },
                { field: 'CardName', headerName: 'Name', flex: 2 },
              ]}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              onRowClicked={(p: any) => handleVendorSelect(p.data)}
              onRowDoubleClicked={(p: any) => handleVendorSelect(p.data)}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Item Picker Dialog */}
      <Dialog open={itemPickerOpen} onClose={() => { setItemPickerOpen(false); setCurrentLineIndex(null); setItemSearchQuery(''); setItemOptions([]); }} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Select Item {currentLineIndex !== null && `- Line ${currentLineIndex + 1}`}
            <IconButton onClick={() => { setItemPickerOpen(false); setCurrentLineIndex(null); setItemSearchQuery(''); setItemOptions([]); }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            placeholder="Search items by code or name..."
            value={itemSearchQuery}
            onChange={(e) => setItemSearchQuery(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#999' }} />
            }}
          />
          <Box className="ag-theme-alpine" sx={{ height: 400, width: '100%' }}>
            <AgGridReact
              columnDefs={itemColumnDefs}
              rowData={itemOptions}
              pagination={true}
              paginationPageSize={20}
              onRowClicked={(params) => handleItemSelect(params.data)}
              loading={itemSearchLoading}
              rowStyle={{ cursor: 'pointer' }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={inlinePickerOpen} onClose={() => setInlinePickerOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Select {inlinePickerType.toUpperCase()}
            <IconButton onClick={() => setInlinePickerOpen(false)}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            placeholder="Search..."
            value={inlineQuery}
            onChange={(e) => setInlineQuery(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#999' }} /> }}
          />
          <Box className="ag-theme-alpine" sx={{ height: 360, width: '100%' }}>
            <AgGridReact
              rowData={inlineRows}
              columnDefs={inlineColumnDefs as any}
              loading={inlineLoading}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              onRowClicked={(p: any) => chooseInlineValue(p.data)}
              onRowDoubleClicked={(p: any) => chooseInlineValue(p.data)}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={poSearchOpen} onClose={() => { setPoSearchOpen(false); setPoSearchQuery(''); }} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Search PO
            <IconButton onClick={() => { setPoSearchOpen(false); setPoSearchQuery(''); }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            placeholder="Search by PO No, Vendor, Project, Status..."
            value={poSearchQuery}
            onChange={(e) => setPoSearchQuery(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#999' }} /> }}
          />
          <Box className="ag-theme-alpine" sx={{ height: 420, width: '100%' }}>
            <AgGridReact
              rowData={filteredMyPOs.slice(0, 500)}
              columnDefs={[
                { field: 'ponumber', headerName: 'PO Number', flex: 1.2 },
                { field: 'podate', headerName: 'PO Date', flex: 1 },
                { field: 'vendorname', headerName: 'Vendor', flex: 1.5 },
                { field: 'allocationcode', headerName: 'Project/CC', flex: 1 },
                { field: 'status', headerName: 'Status', flex: 1 },
              ]}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              onRowClicked={(p: any) => { const id = Number(p.data?.poid || p.data?.POID || 0); if (id) void loadPOById(id); }}
              onRowDoubleClicked={(p: any) => { const id = Number(p.data?.poid || p.data?.POID || 0); if (id) void loadPOById(id); }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={chargesDialogOpen} onClose={() => setChargesDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Additional Charges
            <IconButton onClick={() => setChargesDialogOpen(false)}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box className="ag-theme-alpine" sx={{ height: 320, width: '100%' }}>
            <AgGridReact
              rowData={chargeRows}
              columnDefs={chargeColumnDefs as any}
              onCellValueChanged={onChargeCellValueChanged}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              pagination
              paginationPageSize={5}
            />
          </Box>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>Charges Total: {chargeTotal.toFixed(2)}</Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Charges marked SAP freight go on the SAP PO (Company Management sets which). The others are estimates for the costing and stay in PRPO.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={discountDialogOpen} onClose={() => setDiscountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Discount
            <IconButton onClick={() => setDiscountDialogOpen(false)}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box className="ag-theme-alpine" sx={{ height: 240, width: '100%' }}>
            <AgGridReact
              rowData={discountRows}
              columnDefs={valuePairColumnDefs as any}
              onCellValueChanged={onDiscountCellValueChanged}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              pagination
              paginationPageSize={5}
            />
          </Box>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Sent to SAP as the PO&apos;s header discount; SAP works out VAT after it.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={taxDialogOpen} onClose={() => setTaxDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Tax (estimate)
            <IconButton onClick={() => setTaxDialogOpen(false)}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box className="ag-theme-alpine" sx={{ height: 240, width: '100%' }}>
            <AgGridReact
              rowData={taxRows}
              columnDefs={valuePairColumnDefs as any}
              onCellValueChanged={onTaxCellValueChanged}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              pagination
              paginationPageSize={5}
            />
          </Box>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            An estimate: shown on the PO, the PDF and the e-mails, never sent to SAP. SAP adds VAT itself from each line&apos;s tax code.
          </Typography>
        </DialogContent>
      </Dialog>

      <CopyPRDialog
        open={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        onSelectPR={handleCopyPRToPO}
        postedOnly={true}
        title="Copy From Posted PR"
      />

      <MuiMenu
        anchorEl={gridMenuAnchor}
        open={Boolean(gridMenuAnchor)}
        onClose={() => {
          setGridMenuAnchor(null);
          setColumnsOpen(false);
          setDensityOpen(false);
        }}
      >
        <MuiMenuItem onClick={() => { setColumnsOpen(true); setDensityOpen(false); }}>Columns</MuiMenuItem>
        <MuiMenuItem onClick={() => { setDensityOpen(true); setColumnsOpen(false); }}>Density</MuiMenuItem>
      </MuiMenu>

      <Popover
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        anchorEl={gridMenuAnchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, minWidth: 240 }}>
          {(['itemcode','itemdescription','quantity','openqty','closedqty','uomcode','unitprice','discpct','discamt','linetotal','accountcode','warehouse','deliverydate','projectcode','dim1','dim2','dim3','dim4','dim5','lineremarks'] as const).map((f) => (
            <FormControlLabel
              key={f}
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibilityModel[f] !== false}
                  onChange={(e) => setColumnVisibilityModel((prev) => ({ ...prev, [f]: e.target.checked }))}
                />
              }
              label={f}
              sx={{ display: 'block', m: 0 }}
            />
          ))}
        </Box>
      </Popover>

      <Popover
        open={densityOpen}
        onClose={() => setDensityOpen(false)}
        anchorEl={gridMenuAnchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5 }}>
          <RadioGroup row value={gridDensity} onChange={(e) => setGridDensity(e.target.value as any)}>
            <FormControlLabel value="compact" control={<Radio size="small" />} label="Compact" />
            <FormControlLabel value="standard" control={<Radio size="small" />} label="Standard" />
            <FormControlLabel value="comfortable" control={<Radio size="small" />} label="Comfortable" />
          </RadioGroup>
        </Box>
      </Popover>

      <MuiMenu
        open={!!contextMenu}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseX, left: contextMenu.mouseY } : undefined}
      >
        <MuiMenuItem onClick={() => { if (contextMenu?.rowIndex !== null && contextMenu?.rowIndex !== undefined) handleCopyRow(contextMenu.rowIndex); setContextMenu(null); }}>
          Copy Row
        </MuiMenuItem>
        <MuiMenuItem disabled={!canEditCurrentPO} onClick={() => { if (contextMenu?.rowIndex !== null && contextMenu?.rowIndex !== undefined) handleCutRow(contextMenu.rowIndex); setContextMenu(null); }}>
          Cut Row
        </MuiMenuItem>
        <MuiMenuItem onClick={() => { if (contextMenu?.rowIndex !== null && contextMenu?.rowIndex !== undefined) handlePasteRow(contextMenu.rowIndex); setContextMenu(null); }} disabled={!clipboardRow || !canEditCurrentPO}>
          Paste Row
        </MuiMenuItem>
        <MuiMenuItem onClick={() => {
          if (!canEditCurrentPO) {
            setContextMenu(null);
            return;
          }
          const idx = contextMenu?.rowIndex;
          if (idx !== null && idx !== undefined) {
            setLines((prev) => {
              const n = prev.filter((_, i) => i !== idx);
              return n.length ? n : [createEmptyLine()];
            });
          }
          setContextMenu(null);
        }} disabled={!canEditCurrentPO}>
          Delete Row
        </MuiMenuItem>
      </MuiMenu>

      <Backdrop open={submitting} sx={{ zIndex: 9999 }}>
        <CircularProgress />
      </Backdrop>

      <Dialog
        open={centerPopup.open}
        onClose={() => {
          setCenterPopup((prev) => ({ ...prev, open: false }));
          if (postSubmitNavigateId) {
            navigate(`/po/${postSubmitNavigateId}`);
            setPostSubmitNavigateId(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: centerPopup.severity === 'error' ? 'error.main' : 'success.main' }}>
          {centerPopup.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: centerPopup.details.length ? 1 : 2 }}>
            {centerPopup.message}
          </Typography>
          {centerPopup.details.map((d, i) => (
            <Typography key={`${d}-${i}`} variant="body2" sx={{ mb: 0.5 }}>
              {i + 1}. {d}
            </Typography>
          ))}
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                setCenterPopup((prev) => ({ ...prev, open: false }));
                if (postSubmitNavigateId) {
                  navigate(`/po/${postSubmitNavigateId}`);
                  setPostSubmitNavigateId(null);
                }
              }}
            >
              OK
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default CreatePO;
